import {
  Box,
  Button,
  Grid,
  Td,
  Text,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, {useState} from "react";
import {useMutation, useQueryClient} from "react-query";

import {postCandidateSetActive, postCandidatesSetActive} from "../../shared/Api";
import {errorToast, successToast} from "../../shared/helper/ToastHelper";
import {useAuthentication} from "../../shared/hooks/AuthenticationProvider";
import {Authority, CandidateStatus, Paths} from "../../shared/model/Constants";
import {AutomatedCandidateDataDto, CandidateDataDto, CandidateDataIds, CandidateSummaryDto} from "../../shared/model/Dto";
import {adminKeys} from "../../shared/QueryKeys";
import {displayCandidateStatusBadge, displayTime} from "../../shared/Utils";

type CandidateSummaryItemProps = {
  candidateSummary: CandidateSummaryDto;
};
const EDICandidateSummaryItem = (props: CandidateSummaryItemProps) => {
  const authentication = useAuthentication();

  const isAdmin =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_ADMIN);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [allActive, setAllActive] = useState<boolean>( checkCandidatesActive() );

  const setCandidatesActive = useMutation(
    ({newActive}: any) => {
      return postCandidatesSetActive( props.candidateSummary.username,
          props.candidateSummary.candidates,
          props.candidateSummary.automatedCandidates,
          newActive) },
    {
      onSuccess: (data, variables) => {
        for (const candidate of props.candidateSummary.candidates) {
          candidate.active = variables.newActive;
        }
        for (const candidate of props.candidateSummary.automatedCandidates) {
          candidate.active = variables.newActive;
        }
        setAllActive(variables.newActive);

        return queryClient.invalidateQueries(adminKeys.candidateActive());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidatos");
      },
    });

  const setCandidateActive = useMutation(
    ({publicId, candidateToken, active, isAutomated}: any) =>
      postCandidateSetActive(props.candidateSummary.username, publicId, candidateToken, active, isAutomated),
    {
      onSuccess: (data, variables) => {
        let candidate;
        if (variables.isAutomated) {
          candidate= props.candidateSummary.automatedCandidates.find(c => c.testPublicId === variables.publicId);
        } else {
          candidate= props.candidateSummary.candidates.find(c => c.testPublicId === variables.publicId &&
              c.token === variables.candidateToken  );
        }
        if (candidate)
          candidate.active = variables.active;
        setAllActive(checkCandidatesActive());

        return queryClient.invalidateQueries(adminKeys.candidateActive());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });

  const institution = props.candidateSummary.institution !== null ? props.candidateSummary.institution : "";
  const region = props.candidateSummary.region !== null ? props.candidateSummary.region : "";
  const institutionDisplay = institution && region ? institution+" - "+region : institution+region

  function checkCandidatesActive() {
    return props.candidateSummary.candidates.every(candidate => candidate.active === true) &&
           props.candidateSummary.automatedCandidates.every(candidate => candidate.active === true);
  }

/*
  // Rerender to make sure active is updated
  // noinspection SillyAssignmentJS
  props.candidateSummary.candidateR.active = props.candidateSummary.candidateR.active;
  // noinspection SillyAssignmentJS
  props.candidateSummary.candidateL.active = props.candidateSummary.candidateL.active;
  // noinspection SillyAssignmentJS
  props.candidateSummary.candidateS.active = props.candidateSummary.candidateS.active;
  */
  // Rerender to make sure active is updated
  for (const candidate of props.candidateSummary.candidates) {
    // noinspection SillyAssignmentJS
    candidate.active = candidate.active;
  }
  for (const candidate of props.candidateSummary.automatedCandidates) {
    // noinspection SillyAssignmentJS
    candidate.active = candidate.active;
  }

  return (
    <>
      <Tr key={props.candidateSummary.username}>
        <Td>{props.candidateSummary.username}</Td>
        <Td>{props.candidateSummary.name}</Td>
        <Td>{institutionDisplay}</Td>
        <Td>{props.candidateSummary.userGroup}</Td>
        <Td>
          <Button onClick={()=>setCandidatesActive.mutate({
                      newActive: !allActive
                    })}
          colorScheme={allActive ? 'red' : 'green'}
          >
            {allActive ? 'Deshabilitar Todos' : 'Habilitar Todos'}
          </Button>
        </Td>

        <Td> <VStack align="stretch">
            {props.candidateSummary.candidates.map((candidate) => (
              <Box
                key={candidate.testPublicId}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.100"
              >
                <Grid templateColumns={`repeat(5, 1fr)`} placeItems="center" columnGap={2}>
                  <VStack textAlign="center">
                    <Text>{candidate.testName}</Text>
                    <Text>({candidate.skillType})</Text>
                  </VStack>
                  {displayCandidateStatusBadge(candidate.status)}
                  <VStack textAlign="center">
                    <Text>{displayTime(candidate.startTime)}</Text>
                    <Text>{displayTime(candidate.endTime)}</Text>
                  </VStack>
                  <Text>{candidate.lastAnsweredQuestion}</Text>
                      <>{candidate.status !== CandidateStatus.TEST_COMPLETE &&
                          candidate.status !== null && candidate.active &&
                                      <Button onClick={()=>setCandidateActive.mutate({
                                        publicId: candidate.testPublicId,
                                        candidateToken: candidate.token,
                                        active: false, isAutomated:false
                                      })} colorScheme='red'
                                              isLoading={setCandidateActive.isLoading} >
                                        Deshabilitar
                                      </Button>
                                    } {candidate.status !== CandidateStatus.TEST_COMPLETE &&
                                    candidate.status !== null &&
                                    !candidate.active &&
                                    <Button onClick={()=>setCandidateActive.mutate({
                                      publicId: candidate.testPublicId,
                                      candidateToken: candidate.token,
                                      active: true, isAutomated:false
                                    })} colorScheme='green'
                                            isLoading={setCandidateActive.isLoading} >
                                      Habilitar
                                    </Button>
                                    }
                      </>
                </Grid>
              </Box>
            ))}
          </VStack>

        <VStack align="stretch">
            {props.candidateSummary.automatedCandidates.map((candidate) => (
              <Box
                key={candidate.testPublicId}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.100"
              >
                <Grid templateColumns={`repeat(5, 1fr)`} placeItems="center" columnGap={2}>
                  <VStack textAlign="center">
                    <Text>{candidate.testName}</Text>
                    <Text>({candidate.skillType})</Text>
                  </VStack>
                  {displayCandidateStatusBadge(candidate.status)}
                  <VStack textAlign="center">
                    <Text>{displayTime(candidate.startTime)}</Text>
                    <Text>{displayTime(candidate.endTime)}</Text>
                  </VStack>
                  { isAdmin ? <Text>{candidate.reportUrl}</Text> : <Text></Text>}
                  <>{candidate.status !== CandidateStatus.TEST_COMPLETE &&
                          candidate.status !== null && candidate.active &&
                                      <Button onClick={()=>setCandidateActive.mutate({
                                        publicId: candidate.testPublicId,
                                        candidateToken: "",
                                        active: false, isAutomated:true
                                      })} colorScheme='red'
                                              isLoading={setCandidateActive.isLoading} >
                                        Deshabilitar
                                      </Button>
                                    } {candidate.status !== CandidateStatus.TEST_COMPLETE &&
                                    candidate.status !== null &&
                                    !candidate.active &&
                                    <Button onClick={()=>setCandidateActive.mutate({
                                      publicId: candidate.testPublicId,
                                      candidateToken: "",
                                      active: true, isAutomated:true
                                    })} colorScheme='green'
                                            isLoading={setCandidateActive.isLoading} >
                                      Habilitar
                                    </Button>
                                    }
                      </>

                </Grid>
              </Box>
            ))}
          </VStack>
        </Td>
      </Tr>

    </>
  );
};
export default EDICandidateSummaryItem;